/**
 * The week a timesheet is read in.
 *
 * Monday-first, because a timesheet is a working week and starting it on Sunday
 * splits every weekend across two pages. Dates are handled as local calendar
 * days: the entries are stored in UTC, but "which week is this" is a question
 * about the person's own Monday.
 */

export function startOfWeek(date = new Date()) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  // getDay() is 0 for Sunday, so Sunday goes back six days rather than forward one.
  const offset = (d.getDay() + 6) % 7
  d.setDate(d.getDate() - offset)
  return d
}

export function addDays(date, days) {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

/** YYYY-MM-DD in the local calendar, which is what the API's from/to expect. */
export function isoDay(date) {
  const d = new Date(date)
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function weekRange(anchor) {
  const from = startOfWeek(anchor)
  return { from, to: addDays(from, 6) }
}

export function weekLabel(anchor) {
  const { from, to } = weekRange(anchor)
  const sameMonth = from.getMonth() === to.getMonth()
  const fmt = (d, withMonth) =>
    d.toLocaleDateString(undefined, withMonth ? { day: "numeric", month: "short" } : { day: "numeric" })
  const year = to.getFullYear() === new Date().getFullYear() ? "" : ` ${to.getFullYear()}`
  return `${fmt(from, !sameMonth)} – ${fmt(to, true)}${year}`
}

export function isToday(date) {
  return isoDay(date) === isoDay(new Date())
}

/** The seven days of the week holding `anchor`, each with the entries that fall on it. */
export function groupByDay(entries, anchor) {
  const { from } = weekRange(anchor)
  const days = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(from, i)
    return { date, key: isoDay(date), entries: [], seconds: 0 }
  })
  const byKey = new Map(days.map((d) => [d.key, d]))

  for (const entry of entries) {
    const day = byKey.get(isoDay(entry.started_at))
    if (!day) continue
    day.entries.push(entry)
    day.seconds += Number(entry.duration_seconds) || 0
  }
  return days
}
